import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, combineLatest, NEVER, Observable, Subscription } from 'rxjs';
import { map, filter, tap, take, mapTo } from 'rxjs/operators';

import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { LeftSidebarService } from '../../../Common/Layout/services/left-sidebar.service';
import { Mode } from '../../../Common/Layout/enums/mode.enum';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarLocalizations } from '../../../Common/Layout/models/left-sidebar-localizations.model';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { DiscoverRouteModel } from '../../DiscoverRouter/models/discover-route.model';
import { DiscoverRoutingService } from '../../DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteTypeEnum } from '../../DiscoverRouter/enums/discover-route-type.enum';
import { TargetingSearch } from '../../Targeting/models/targeting-search.model';
import { TargetingSearchesService } from '../../Targeting/services/targeting-searches.service';
import { TargetingSearchList } from '../../Targeting/models/targeting-search-list.model';
import { TargetingListList } from '../../Targeting/models/targeting-list-list.model';
import { TargetingListService } from '../../Targeting/services/targeting-list.service';
import { NewEntityDialogComponent, NewEntityDialogListItem } from '../../../Common/Dialogs/components/NewEntityDialog/new-entity-dialog.component';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import { DistributionService } from '../../../Common/TargetingShared/services/distribution.service';
import { DistributionSummaryModel } from '../../../Common/TargetingShared/model/distribution-summary-model';
import { DistributionStatus } from '../../../Common/TargetingShared/enum/distribution-status.enum';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { TargetingFiltersEnum } from '../../../Common/TargetingShared/enum/targeting-filters.enum';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import {
    UserAppPreferenceCommonFilters,
    UserAppPreferenceDistributionsFilters,
    UserAppPreferenceSavedSearchesFilters,
    UserAppPreferenceTargetedListsFilters
} from '../../../Core/Users/models/user-app-preferences-filters.model';
import { TargetedListTypesEnum } from '../../../Common/TargetingShared/enum/targeted-list-types.enum';
import { UserAppTargetingPreferencesModel } from '../../../Core/Users/models/user-app-preferences.model';
import { ListEntitiesVisibilityEnum } from '../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { TargetingList } from '../../Targeting/models/targeting-list.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DataConnectionService } from '../../../Core/Users/services/data-connection.service';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';
import { AmplifyRoutingService } from '../../../Amplify/Core/services/amplify-routing.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { StoriesConstants } from '../../../News/Newsroom/components/stories/stories-constants';

type GenericLeftSidebarItem = AuditableModel & LeftSidebarListItemConvertableModel;


const discoverModeToUserPreferenceTypeMap = new Map<Mode, keyof UserAppTargetingPreferencesModel>([
    [Mode.distributions, 'distributionsPrefs'],
    [Mode.searches, 'savedSearchesPrefs'],
    [Mode.targetingLists, 'targetedListsPrefs'],
]);


interface DiscoverLeftSidebarModeConfig<T = LeftSidebarListItemConvertableModel> {
    mode: Mode;
    routeTypes: DiscoverRouteTypeEnum[];
    isLoading: boolean;
    isComplete: boolean;
    listItemSource: T[];
    localizations: LeftSidebarLocalizations;
    getButtons: (item: any) => LeftSidebarListItemButton[];
    isAmplifyButtonEnabled: boolean;
    amplifyAction: () => void;
    filters?: FilterTypeModel[];
    setPreferenceFilters: (UserAppPreferenceCommonFilters) => void;
    prepareFilters?: () => void; // set name, get checkbox options, etc.
    applyFilters?: (items: T[]) => T[];
    sortFields: LocalizedEnum[];
}

@Injectable()
export class DiscoverLeftSidebarService extends LeftSidebarService implements OnDestroy {

    private currentModeConfig: DiscoverLeftSidebarModeConfig;
    private dropdownItems: DropdownOptionModel[];
    private dropdownItemsProvider: BehaviorSubject<DropdownOptionModel[]>;
    private selectedDropdownItem: DropdownOptionModel;
    private selectedDropdownItemProvider: BehaviorSubject<DropdownOptionModel>;

    private targetingSearches: TargetingSearchList[] = [];
    private targetingLists: TargetingListList[] = [];
    private distributions: DistributionSummaryModel[] = [];

    protected readonly modesWithFixedTopItem = [Mode.distributions, Mode.targetingLists];

    private listIsComplete: boolean;
    private listIsCompleteProvider: BehaviorSubject<boolean>;
    private selectedListItem: LeftSidebarListItem;
    private selectedListItemProvider: BehaviorSubject<LeftSidebarListItem>;
    private reorderEnabled: boolean;
    private reorderEnabledProvider: BehaviorSubject<boolean>;
    private buttonEnabled: boolean;
    private buttonEnabledProvider: BehaviorSubject<boolean>;
    private hiddenListItemsProvider: BehaviorSubject<LeftSidebarListItem[]>;
    private hiddenlistItems: LeftSidebarListItem[];

    private localizations: LeftSidebarLocalizations;
    private localizationsProvider: BehaviorSubject<LeftSidebarLocalizations>;

    private searchDirty: boolean;

    private subscriptions: Subscription[] = [];

    private itemActionEdit: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-pen',
        key: ActionsEnum.edit,
        label: 'Lhp.actions.edit',
        tooltip: 'Discover.leftSidebar.listItemAction.edit',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.edit, icon: ''})
    };
    private itemActionRename: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-pen',
        key: ActionsEnum.rename,
        label: 'Lhp.actions.rename',
        tooltip: 'Discover.leftSidebar.listItemAction.rename',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.rename, icon: ''})
    };
    private itemActionDelete: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-trash-can',
        key: ActionsEnum.delete,
        label: 'Lhp.actions.delete',
        tooltip: 'Discover.leftSidebar.listItemAction.delete',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.delete, icon: ''})
    };
    private itemActionCopy: LeftSidebarListItemButton = {
        icon: 'fa-kit-duotone fa-agpr-copy',
        key: ActionsEnum.copy,
        label: 'Lhp.actions.copy',
        tooltip: 'Discover.leftSidebar.listItemAction.copy',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.copy, icon: ''})
    };

    private itemActionShow: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye',
        key: ActionsEnum.show,
        label: 'Lhp.actions.show',
        tooltip: 'Discover.leftSidebar.listItemAction.show',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.show, icon: ''})
    };

    private itemActionHide: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye-slash',
        key: ActionsEnum.hide,
        label: 'Lhp.actions.hide',
        tooltip: 'Discover.leftSidebar.listItemAction.hide',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.hide, icon: ''})
    };

    private itemActionMove: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.move,
        label: 'Lhp.actions.move',
        tooltip: 'Discover.leftSidebar.listItemAction.move',
        command: (event: any) => this.moveListItem(event.data)
    };

    private itemActionFavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.favorite,
        label: 'Lhp.actions.favorite',
        tooltip: 'Discover.leftSidebar.listItemAction.favorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.favorite, icon: ''})
    };

    private itemActionUnfavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.unfavorite,
        label: 'Lhp.actions.unfavorite',
        tooltip: 'Discover.leftSidebar.listItemAction.unfavorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.unfavorite, icon: ''})
    };

    private readonly listTypeFilter = new FilterTypeCheckboxModel<TargetedListTypesEnum>(
        'listType',
        'Discover.leftSidebar.filter.listType',
        [
            {
                label: 'Discover.leftSidebar.filter.listType.option.both',
                value: TargetedListTypesEnum.both
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.contact',
                value: TargetedListTypesEnum.contact
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.outlet',
                value: TargetedListTypesEnum.outlet
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.empty',
                value: TargetedListTypesEnum.empty
            }
        ]
    );

    private readonly searchTypeFilter = new FilterTypeCheckboxModel<SearchModeEnum>(
        'searchType',
        'Discover.leftSidebar.filter.searchType',
        [
            {
                label: 'Discover.leftSidebar.filter.searchType.option.contact',
                value: SearchModeEnum.contact
            },
            {
                label: 'Discover.leftSidebar.filter.searchType.option.outlet',
                value: SearchModeEnum.outlet
            }
        ]
    );


    private readonly deliveryStatusFilter = new FilterTypeCheckboxModel<DistributionStatus>(
        TargetingFiltersEnum.status,
        'Discover.targeting.filter.status',
        [
            {
                label: 'Discover.deliveryStatusFilter.draft',
                value: DistributionStatus.draft
            },
            {
                label: 'Discover.deliveryStatusFilter.scheduled',
                value: DistributionStatus.scheduled
            },
            {
                label: 'Discover.deliveryStatusFilter.sent',
                value: DistributionStatus.sent
            },
            {
                label: 'Discover.deliveryStatusFilter.failed',
                value: DistributionStatus.failed
            },
        ]
    );



    private currentRoute: DiscoverRouteModel;

    private readonly discoverLeftSidebarModeConfigMap: Map<Mode, DiscoverLeftSidebarModeConfig> = new Map([
        [
            Mode.searches,
            {
                mode: Mode.searches,
                routeTypes: [
                    DiscoverRouteTypeEnum.targetingSearchHype,
                    DiscoverRouteTypeEnum.targetingSearch,
                    DiscoverRouteTypeEnum.targetingSearchUpdate,
                    DiscoverRouteTypeEnum.targetingResults,
                    DiscoverRouteTypeEnum.targetingSavedSearch,
                    DiscoverRouteTypeEnum.targetingSavedSearchResults,
                ],
                isComplete: true,
                isLoading: true,
                listItemSource: this.targetingSearches,
                isAmplifyButtonEnabled: true,
                getButtons: (item) => {
                    // if item is not the fixed one
                    return [
                        this.itemActionRename,
                        this.itemActionEdit,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];
                },
                localizations: {
                    amplifyButton: 'Discover.leftSidebar.searches.new',
                    itemsHeading: 'Discover.leftSidebar.searches',
                    statusHeading: '',
                    noItems: 'Discover.leftSidebar.searches.empty'
                },
                amplifyAction: () => {
                    this.targetingSearchesService.canDeactivate$().subscribe(leave => {
                        if (leave) {
                            this.discoverRoutingService.gotoTargetingSearch();
                        }
                    });
                },
                filters: [
                    this.nameFilter,
                    this.searchTypeFilter,
                    this.createdByFilter,
                    this.createdDateFilter,
                    this.modifiedByFilter,
                    this.modifiedDateFilter,
                    this.visibilityFilter,
                    this.displayGroupFilter
                ],
                setPreferenceFilters: (userFilters: UserAppPreferenceSavedSearchesFilters) => {
                    this.nameFilter.setCurrentValue(userFilters.name);
                    this.searchTypeFilter.setCurrentValue(userFilters.type);
                    this.createdByFilter.setCurrentValue(userFilters.createdBy);
                    this.createdDateFilter.setCurrentValue(userFilters.createdDate);
                    this.modifiedByFilter.setCurrentValue(userFilters.modifiedBy);
                    this.modifiedDateFilter.setCurrentValue(userFilters.modifiedDate);
                },
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('Discover.leftSidebar.searches.quickFilter');
                    this.nameFilter.label = 'Discover.leftSidebar.searches.filter.name';

                    this.getSearchTypeFilterCounts(this.targetingSearches);
                    this.getAuditableFilterOptions(this.targetingSearches);
                },
                applyFilters: (items: TargetingSearchList[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applySearchTypeFitler(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    filteredItems = this.applyVisibilityFilter(filteredItems);
                    return filteredItems;
                },
                sortFields: [this.sortFieldName, this.sortFieldCreatedDate]
            }
        ],
        [
            Mode.discoverContacts,
            {
                mode: Mode.discoverContacts,
                routeTypes: [
                    DiscoverRouteTypeEnum.discoverContacts
                ],
                isComplete: true,
                isLoading: true,
                listItemSource: this.targetingSearches,
                isAmplifyButtonEnabled: true,
                getButtons: (item) => {
                    // if item is not the fixed one
                    return [
                        this.itemActionRename,
                        this.itemActionEdit,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];
                },
                localizations: {
                    amplifyButton: 'Discover.leftSidebar.searches.new',
                    itemsHeading: 'Discover.leftSidebar.searches',
                    statusHeading: '',
                    noItems: 'Discover.leftSidebar.searches.empty'
                },
                amplifyAction: () => {
                    this.targetingSearchesService.canDeactivate$().subscribe(leave => {
                        if (leave) {
                            const newEntityDialogConfig = {
                                inputs: {
                                    itemsFetcher: this.targetingSearchesService.all$.pipe(
                                        filter(searches => searches != null),
                                        map(searches => {
                                            return searches.map(search => <NewEntityDialogListItem>{
                                                value: search.id,
                                                label: search.name,
                                                type: 'Discover.dialogs.newSearch.entityName',
                                                modifiedDate: search.modifiedDate,
                                                createdDate: search.createdDate
                                            });
                                        })
                                    ),
                                    width: 500,
                                    iconClass: 'agpr-i-line-targeting',
                                    localizations: {
                                        header: 'Discover.dialogs.newSearch.header',
                                        description: 'Discover.dialogs.newSearch.description',
                                        entityName: 'Discover.dialogs.newSearch.entityName',
                                        newItemButton: 'Discover.dialogs.newSearch.newItemButton',
                                        or: 'Discover.dialogs.newSearch.or',
                                        itemListHeader: 'Discover.dialogs.newSearch.itemListHeader',
                                        itemListDescription: 'Discover.dialogs.newSearch.itemListDescription',
                                        itemListFilterPlaceholder: 'Discover.dialogs.newSearch.itemListFilterPlaceholder',
                                        clone: 'Discover.dialogs.newSearch.clone',
                                        create: 'Discover.dialogs.newSearch.create',
                                        cancel: 'Discover.dialogs.newSearch.cancel'
                                    }
                                },
                                outputs: {
                                    dialogClose: () => { },
                                    onClone: (searchToClone: NewEntityDialogListItem) => {
                                        this.discoverRoutingService.gotoTargetingSearchWithParams({ copy: searchToClone.value });
                                    },
                                    onCreate: () => {
                                        this.discoverRoutingService.gotoTargetingSearch();
                                    }
                                }
                            };
                            this.coreDialogService.open(
                                NewEntityDialogComponent,
                                newEntityDialogConfig
                            );
                        }
                    });
                },
                filters: [
                    this.nameFilter,
                    this.searchTypeFilter,
                    this.createdByFilter,
                    this.createdDateFilter,
                    this.modifiedByFilter,
                    this.modifiedDateFilter,
                    this.visibilityFilter
                ],
                setPreferenceFilters: (userFilters: UserAppPreferenceSavedSearchesFilters) => {
                    this.nameFilter.setCurrentValue(userFilters.name);
                    this.searchTypeFilter.setCurrentValue(userFilters.type);
                    this.createdByFilter.setCurrentValue(userFilters.createdBy);
                    this.createdDateFilter.setCurrentValue(userFilters.createdDate);
                    this.modifiedByFilter.setCurrentValue(userFilters.modifiedBy);
                    this.modifiedDateFilter.setCurrentValue(userFilters.modifiedDate);
                },
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('Discover.leftSidebar.searches.quickFilter');
                    this.nameFilter.label = 'Discover.leftSidebar.searches.filter.name';

                    this.getSearchTypeFilterCounts(this.targetingSearches);
                    this.getAuditableFilterOptions(this.targetingSearches);
                },
                applyFilters: (items: TargetingSearchList[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applySearchTypeFitler(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    filteredItems = this.applyVisibilityFilter(filteredItems);
                    return filteredItems;
                },
                sortFields: [this.sortFieldName, this.sortFieldCreatedDate]
            }
        ],
        [
            Mode.targetingLists,
            {
                mode: Mode.targetingLists,
                routeTypes: [
                    DiscoverRouteTypeEnum.targetingListHype,
                    DiscoverRouteTypeEnum.targetingListSearchResult
                ],
                isComplete: true,
                isLoading: true,
                listItemSource: this.targetingLists,
                isAmplifyButtonEnabled: true,
                getButtons: (item) => {
                    return [
                        this.itemActionRename,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];
                },
                localizations: {
                    amplifyButton: 'Discover.leftSidebar.targetingLists.new',
                    itemsHeading: 'Discover.leftSidebar.targetingLists',
                    statusHeading: '',
                    noItems: 'Discover.leftSidebar.targetingLists.empty'
                },
                amplifyAction: () => {
                    const io: NewResourceDialogModel = {
                        inputs: {
                            heading: 'Discover.dialogs.newList.heading',
                            description: 'Discover.dialogs.newList.description',
                            placeholder: 'Discover.dialogs.newList.placeholder',
                            okLabel: 'Discover.dialogs.newList.createList',
                            cancelLabel: 'Discover.dialogs.universal.cancelLabel',
                            width: 300,
                            disabledButtonTooltip: 'Discover.dialogs.newList.listNameRequired',
                            submit: (listName: string, group: DisplayGroupModel) => {

                                const newList = new TargetingList({name: listName, displayGroupId: group?.id});

                                return this.targetingListService.create(newList, group).pipe(
                                    tap((id) => {
                                        if (id) {
                                            this.discoverRoutingService.gotoListSearchResults(id);
                                        }
                                    })
                                );
                            },
                            observableFormValidator: (listName: string) => {
                                return this.targetingListService.isNameDuplicated$(listName).pipe(
                                    map((isDuplicate) => {
                                        if (isDuplicate) {
                                            return ['Discover.dialogs.newList.duplicate'];
                                        } else {
                                            return [];
                                        }
                                    })
                                );
                            }
                        },
                        outputs: {}
                    };
                    this.coreDialogService.open(NewResourceDialogComponent, io);
                },
                filters: [
                    this.nameFilter,
                    this.listTypeFilter,
                    this.createdByFilter,
                    this.createdDateFilter,
                    this.modifiedByFilter,
                    this.modifiedDateFilter,
                    this.visibilityFilter,
                    this.displayGroupFilter
                ],
                setPreferenceFilters: (userFilters: UserAppPreferenceTargetedListsFilters) => {
                    this.nameFilter.setCurrentValue(userFilters.name);
                    this.listTypeFilter.setCurrentValue(userFilters.type);
                    this.createdByFilter.setCurrentValue(userFilters.createdBy);
                    this.createdDateFilter.setCurrentValue(userFilters.createdDate);
                    this.modifiedByFilter.setCurrentValue(userFilters.modifiedBy);
                    this.modifiedDateFilter.setCurrentValue(userFilters.modifiedDate);
                },
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('Discover.leftSidebar.targetingLists.quickFilter');
                    this.nameFilter.label = 'Discover.leftSidebar.targetingLists.filter.name';

                    this.getListTypeFilterCounts(this.targetingLists);
                    this.getAuditableFilterOptions(this.targetingLists);
                },
                applyFilters: (items: TargetingListList[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applyListTypeFilters(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    filteredItems = this.applyVisibilityFilter(filteredItems);
                    return filteredItems;
                },
                sortFields: [this.sortFieldName, this.sortFieldCreatedDate, this.sortFieldResult]
            }
        ],
        [
            Mode.distributions,
            {
                mode: Mode.distributions,
                routeTypes: [
                    DiscoverRouteTypeEnum.distributionHype,
                    DiscoverRouteTypeEnum.distributionNew,
                    DiscoverRouteTypeEnum.newDistribution,
                    DiscoverRouteTypeEnum.distributionEdit,
                    DiscoverRouteTypeEnum.distributionReport
                ],
                isComplete: true,
                isLoading: true,
                listItemSource: this.distributions,
                isAmplifyButtonEnabled: true,
                getButtons: (item) => {
                    return (item.status === DistributionStatus.draft || item.status === DistributionStatus.scheduled) ? [
                        this.itemActionRename,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ] : [
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];
                },
                localizations: {
                    amplifyButton: 'Discover.leftSidebar.distributions.new',
                    itemsHeading: 'Discover.leftSidebar.distributions',
                    statusHeading: 'Discover.leftSidebar.distributions.status',
                    noItems: 'Discover.leftSidebar.distributions.empty'
                },
                amplifyAction: () => {
                    this.distributionService.showExitDialog().subscribe(leave => {
                        if (leave) {
                            this.discoverRoutingService.goToNewDistribution();
                        }
                    });
                },
                filters: [
                    this.nameFilter,
                    this.deliveryStatusFilter,
                    this.createdByFilter,
                    this.createdDateFilter,
                    this.modifiedByFilter,
                    this.modifiedDateFilter,
                    this.visibilityFilter,
                    this.displayGroupFilter
                ],
                setPreferenceFilters: (userFilters: UserAppPreferenceDistributionsFilters) => {
                    this.nameFilter.setCurrentValue(userFilters.name);
                    this.deliveryStatusFilter.setCurrentValue(userFilters.status);
                    this.createdByFilter.setCurrentValue(userFilters.createdBy);
                    this.createdDateFilter.setCurrentValue(userFilters.createdDate);
                    this.modifiedByFilter.setCurrentValue(userFilters.modifiedBy);
                    this.modifiedDateFilter.setCurrentValue(userFilters.modifiedDate);
                },
                prepareFilters: () => {
                    this.setQuickFilterPlaceholder('Discover.leftSidebar.distributions.quickFilter');
                    this.nameFilter.label = 'Discover.leftSidebar.distributions.filter.name';

                    // TODO this.getDistributionTypeFilterCounts(this.distributions);
                    this.getDeliveryStatusTypeFilterCounts(this.distributions);
                    this.getAuditableFilterOptions(this.distributions);
                },
                applyFilters: (items: TargetingListList[]) => {
                    let filteredItems = items;
                    filteredItems = this.applyNameFilters(filteredItems);
                    filteredItems = this.applyListTypeFilters(filteredItems);
                    filteredItems = this.applyStatusFilters(filteredItems);
                    filteredItems = this.applyAuditableFilters(filteredItems);
                    filteredItems = this.applyVisibilityFilter(filteredItems);
                    return filteredItems;
                },
                sortFields: [this.sortFieldName, this.sortFieldCreatedDate, this.sortFieldStatus]
            }
        ],
    ]);
    private readonly modeConfigs = Array.from(this.discoverLeftSidebarModeConfigMap.values());

    private readonly distributionDashboard = new LeftSidebarListItem();

    private readonly allPrivateContacts = new LeftSidebarListItem();

    constructor(
        private router: Router,
        private coreDialogService: CoreDialogService,
        private discoverRoutingService: DiscoverRoutingService,
        private amplifyRoutingService: AmplifyRoutingService,
        private targetingSearchesService: TargetingSearchesService,
        private targetingListService: TargetingListService,
        private distributionService: DistributionService,
        protected usersService: UsersService,
        private userPreferencesService: UserPreferencesService,
        protected displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        private dataConnectionService: DataConnectionService
    ) {
        super(usersService, displayGroupsService, localizationService);
        this.dropdownItems = [
            new DropdownOptionModel('Core.navigation.mediaRelations.savedSearches', Mode.searches),
            new DropdownOptionModel('Core.navigation.mediaRelations.targetedLists', Mode.targetingLists),
            new DropdownOptionModel('Core.navigation.mediaRelations.distributions', Mode.distributions),
            new DropdownOptionModel('Core.navigation.mediaRelations.privateContacts', Mode.privateContacts)
        ];
        this.dropdownItemsProvider = new BehaviorSubject(this.dropdownItems);

        this.selectedDropdownItem = this.dropdownItems[0];
        this.selectedDropdownItemProvider = new BehaviorSubject(this.selectedDropdownItem);
        this.currentMode = this.selectedDropdownItem.value;
        this.modeProvider$ = new BehaviorSubject(this.currentMode);

        const config = this.discoverLeftSidebarModeConfigMap.get(this.currentMode);
        this.currentModeConfig = config;

        // Null will cause it to show the loading animation
        this.listItems = null;
        this.listItemsProvider = new BehaviorSubject(this.listItems);
        this.listItemGroupsProvider = new BehaviorSubject(this.listItemGroups);
        this.listIsComplete = config?.isComplete;
        this.listIsCompleteProvider = new BehaviorSubject(this.listIsComplete);
        this.selectedListItem = null;
        this.selectedListItemProvider = new BehaviorSubject(this.selectedListItem);
        this.reorderEnabled = false;
        this.reorderEnabledProvider = new BehaviorSubject(this.reorderEnabled);
        this.buttonEnabled = config?.isAmplifyButtonEnabled;
        this.buttonEnabledProvider = new BehaviorSubject(this.buttonEnabled);
        this.localizations = this.discoverLeftSidebarModeConfigMap.get(this.currentMode)?.localizations;
        this.localizationsProvider = new BehaviorSubject(this.localizations);
        this.hiddenlistItems = null;
        this.hiddenListItemsProvider = new BehaviorSubject(this.hiddenlistItems);

        this.topListItem = null;
        this.topListItemProvider = new BehaviorSubject<LeftSidebarListItem | null>(this.topListItem);

        this.distributionDashboard.value = DistributionSummaryModel.distributionDashboardId;
        this.distributionDashboard.label = 'Discover.leftSideBar.distributionDashboard';
        this.distributionDashboard.icon = 'fa-duotone fa-regular fa-gauge-high';

        this.allPrivateContacts.value = TargetingListList.allPrivateContactsId;
        this.allPrivateContacts.label = 'Discover.leftSideBar.allPrivateContacts';
        this.allPrivateContacts.icon = 'fa-kit-duotone fa-agpr-privates';

        this.updateSortFields();


        this.createSubscriptions();
    }

    /**
     * Unsubscribes from the subscriptions when the service is destroyed
     */
    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    /**
     * Updates the sidebar based on the route the user has navigated to
     */
    updateFromRoute(route: DiscoverRouteModel) {
        this.currentRoute = route;
        this.selectedListItem = null;

        if (route.type === null) {
            return;
        }

        if (
            route.type === DiscoverRouteTypeEnum.contactEditor
            || route.type === DiscoverRouteTypeEnum.contactProfile
            || route.type === DiscoverRouteTypeEnum.outletProfile
            || route.type === DiscoverRouteTypeEnum.influencerStreams
        ) {
            this.toggleHideSidebar(true);
            return;
        } else {
            this.toggleHideSidebar(false);
        }

        this.toggleHideAmplifyButton(route.type === DiscoverRouteTypeEnum.privateContacts);
        this.toggleHideList(route.type === DiscoverRouteTypeEnum.privateContacts);

        if (route.type === DiscoverRouteTypeEnum.privateContacts) {
            this.setMode(Mode.targetingLists);
            this.selectedListItem = this.allPrivateContacts;
        } else if (route.type === DiscoverRouteTypeEnum.distributionDashboard) {
            this.setMode(Mode.distributions);
            this.selectedListItem = this.distributionDashboard;
        } else {
            const modeConfig = this.modeConfigs.find(mode => mode.routeTypes.includes(route.type));
            this.setMode(modeConfig?.mode);
            this.setSelectedListItem(route);
        }

        this.setTopListItem(route.type, this.currentMode);

        for (const item of this.dropdownItems) {
            if (item.value === this.currentMode) {
                this.selectedDropdownItem = item;
                this.selectedDropdownItemProvider.next(this.selectedDropdownItem);
            }
        }
        // in case the page is in private contact mode
        if (route.type === DiscoverRouteTypeEnum.privateContacts) {
            this.selectedDropdownItem = this.dropdownItems.find(item => {
                return item.value === Mode.privateContacts;
            });
            this.selectedDropdownItemProvider.next(this.selectedDropdownItem);
        } else if (route.type === DiscoverRouteTypeEnum.targetingSearch) {
            this.selectedDropdownItem = this.dropdownItems.find(item => {
                return item.value === Mode.searches;
            });
            this.selectedDropdownItemProvider.next(this.selectedDropdownItem);
        }
        this.selectedListItemProvider.next(this.selectedListItem);
        switch (route.type) {
            case DiscoverRouteTypeEnum.targetingSavedSearch:
            case DiscoverRouteTypeEnum.targetingSavedSearchResults:
            case DiscoverRouteTypeEnum.targetingSearchHype:
            case DiscoverRouteTypeEnum.targetingSearchUpdate:
                this.primaryHeading = 'Discover.leftSidebar.searches';
                break;
            case DiscoverRouteTypeEnum.targetingListHype:
            case DiscoverRouteTypeEnum.targetingListSearchResult:
                this.primaryHeading = 'Discover.leftSidebar.targetingLists';
                break;
            case DiscoverRouteTypeEnum.privateContacts:
            case DiscoverRouteTypeEnum.csvImport:
                this.primaryHeading = 'Discover.leftSidebar.privateContacts';
                break;
            case DiscoverRouteTypeEnum.distributionDashboard:
            case DiscoverRouteTypeEnum.distributionEdit:
            case DiscoverRouteTypeEnum.distributionHype:
            case DiscoverRouteTypeEnum.distributionNew:
            case DiscoverRouteTypeEnum.newDistribution:
                this.primaryHeading = 'Discover.leftSidebar.distributions';
                break;
            case DiscoverRouteTypeEnum.targetingSearch:
            case DiscoverRouteTypeEnum.targetingResults:
            default:
                this.primaryHeading = 'Discover.primaryHeading.search';
                break;
        }
        this.primaryHeading$.next(this.primaryHeading);

    }

    /**
     * Creates the subscriptions
     */
    private createSubscriptions(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (!permissions) {
                    return;
                }

                const dropdownItems: DropdownOptionModel[] = [
                    new DropdownOptionModel('Core.navigation.mediaRelations.savedSearches', Mode.searches),
                    new DropdownOptionModel('Core.navigation.mediaRelations.targetedLists', Mode.targetingLists),
                    new DropdownOptionModel('Core.navigation.mediaRelations.distributions', Mode.distributions),
                    new DropdownOptionModel('Core.navigation.mediaRelations.privateContacts', Mode.privateContacts),
                    new DropdownOptionModel('Core.navigation.mediaRelations.visibilityIntelligence', Mode.visibilityIntelligence)
                ];

                if (permissions.includes(userPermissionsEnum.newswireplus)) {
                    if (permissions.includes(userPermissionsEnum.wireEditorial)) {
                        dropdownItems.splice(3, 0, new DropdownOptionModel('Amplify.leftSidebar.wire.review', Mode.editorial));
                    }
                    dropdownItems.splice(3, 0, new DropdownOptionModel('Core.navigation.mediaRelations.newswire', Mode.wire));
                }

                if (permissions.includes(userPermissionsEnum.newsroom)) {
                    dropdownItems.splice(3, 0, new DropdownOptionModel(StoriesConstants.sidebarSectionNewsroom, Mode.newsroom));
                }
                this.dropdownItems = dropdownItems;
                this.dropdownItemsProvider.next(this.dropdownItems);
            }),
            this.targetingSearchesService.all$.pipe(
                filter(x => !!x)
            ).subscribe(searches => {
                this.targetingSearches.length = 0;
                this.targetingSearches.push(...searches);

                const config = this.discoverLeftSidebarModeConfigMap.get(Mode.searches);
                config.isLoading = false;
            }),
            this.targetingListService.all$.pipe(
                filter(x => !!x)
            ).subscribe(lists => {

                this.targetingLists.length = 0;
                this.targetingLists.push(...lists);

                const config = this.discoverLeftSidebarModeConfigMap.get(Mode.targetingLists);
                config.isLoading = false;
            }),

            combineLatest([
                this.distributionService.getDistributionSummaries(),
                this.dataConnectionService.emailAccountConnectors$,
                this.currentUserStateService.getUser()
            ]).subscribe(([distributions, connectors, user ]) => {
                this.distributions.length = 0;
                distributions?.forEach(distribution => {
                    if (distribution.sendFrom) {
                        connectors.forEach(connector => {
                            if (connector.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.PRIVATE
                                && connector.connectorEmail === distribution.sendFrom.connectionEmail
                                && connector.userId !== user.id
                            ) {
                                distribution.icon = 'fa-solid fa-shield-keyhole';
                                distribution.disabled = true;
                                distribution.tooltip = this.localizationService.transform(
                                    'Discover.leftSidebar.distributions.blocked.private',
                                    {name: distribution.name}
                                );
                            } else if (connector.connectorEmail === distribution.sendFrom.connectionEmail
                                && distribution.status !== DistributionStatus.draft
                                && connector.grant?.requiresReAuthentication
                            ) {
                                distribution.icon = 'fa-solid fa-minus-circle';
                                distribution.disabled = true;
                                distribution.tooltip = this.localizationService.transform(
                                    'Discover.leftSidebar.distributions.blocked.invalid',
                                    {name: distribution.name}
                                );
                            }
                        });
                    }
                    this.distributions.push(distribution);
                });

                const config = this.discoverLeftSidebarModeConfigMap.get(Mode.distributions);
                config.isLoading = false;
            }),
            this.targetingSearchesService.resourceDirty$.subscribe(x => {
                this.searchDirty = x;
            })
        );

        const updateFromRoute$ = this.discoverRoutingService.getCurrentRoute().pipe(
            map(route => {
                this.updateFromRoute(route);
                return this.currentMode;
            })
        );

        this.subscriptions.push(
            this.createApplyFilterStream(
                this.discoverRoutingService.getModeChange(updateFromRoute$),
                this.getItemsAndPrepareFilters.bind(this),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    tap(userAppPrefs => {
                        const config = this.discoverLeftSidebarModeConfigMap.get(this.currentMode);
                        const preferenceType = discoverModeToUserPreferenceTypeMap.get(this.currentMode);
                        if (preferenceType) {
                            config.setPreferenceFilters(userAppPrefs.targetingPrefs[preferenceType].defaultFilters);
                        }
                    })
                ),
            ).subscribe()
        );


    }

    /**
     * Sets selected list item based on the route.  If route value available, sets selected list item value equal to the route value.
     */
    private setSelectedListItem(route: DiscoverRouteModel): void {
        if (route.value) {
            this.selectedListItem = new LeftSidebarListItem();
            this.selectedListItem.value = route.value;
        }
    }

    /**
     * Sets dashboard list item based on the route type.
     */
    private setTopListItem(routeType: DiscoverRouteTypeEnum, mode: Mode): void {

        this.topListItem = null;

        if (routeType === DiscoverRouteTypeEnum.privateContacts) {
            this.topListItem = this.allPrivateContacts;
        }

        if (mode === Mode.distributions) {
            this.topListItem = this.distributionDashboard;
        }

        this.topListItemProvider.next(this.topListItem);
    }
    /**
     * Updates the current mode to the provided mode and begins updating the sidebar to match the provided mode's requirements
    */
    private setMode(mode: Mode, clearOld: boolean = true): void {
        if (this.currentMode === mode) {
            // this.discoverLeftSidebarModeConfigMap.get(mode).prepareFilters();
            return;
        }

        this.currentMode = mode;
        const config = this.discoverLeftSidebarModeConfigMap.get(mode);

        this.currentModeConfig = config;

        if (config) {
            const listItems: LeftSidebarListItem[] = config.listItemSource.map(item => item.toLeftSidebarListItem(config.getButtons(item)));
            if (!config.isLoading) {
                this.listItems = listItems;
                this.listItemsProvider.next(listItems);
                this.listIsComplete = config.isComplete;
                this.listIsCompleteProvider.next(this.listIsComplete);
            }

            this.buttonEnabled = config.isAmplifyButtonEnabled;
            this.buttonEnabledProvider.next(this.buttonEnabled);
            this.setLocalizations(config.localizations);

            if (clearOld) {
                this.resetFilterOptions();
            }
        }

        this.updateSortFields();

        this.modeProvider$.next(this.currentMode);
    }

    updateSortFields(){
        // reset the sort field to name and order to ascending after mode change
        this.sortOrder = SortOrderEnum.ascending;
        this.sortOrder$.next(this.sortOrder);
        this.selectedSortField = this.sortFieldName;
        this.selectedSortField$.next(this.selectedSortField);

        this.lhpSortFields = this.currentModeConfig?.sortFields;
        this.lhpSortFields$.next(this.lhpSortFields?.slice());
    }

    /**
     * Gets a list of items to include in the sidebar dropdown
    */
    getDropdownItems(): Observable<DropdownOptionModel[]> {
        return this.dropdownItemsProvider;
    }

    /**
     * Gets the currently selected dropdown item
    */
    getSelectedDropdownItem(): Observable<DropdownOptionModel> {
        return this.selectedDropdownItemProvider;
    }

    /**
     * Selects an item in the dropdown
     * @param item An item that exists in the dropdown
    */
    selectDropdownItem(item: DropdownOptionModel): void {

        if (this.dropdownItems.indexOf(item) < 0) {
            throw new Error('Item is not in the list');
        }
        this.selectedDropdownItem = item;
        this.selectedDropdownItemProvider.next(item);

        switch (item.value) {
            case Mode.searches:
                this.discoverRoutingService.gotoTargetingHype();
                break;
            case Mode.targetingLists:
                this.discoverRoutingService.gotoListHype();
                break;
            case Mode.privateContacts:
                this.discoverRoutingService.gotoPrivateContacts();
                break;
            case Mode.distributions:
                this.discoverRoutingService.gotoDistributionHype();
                break;
            case Mode.discoverContacts:
                this.discoverRoutingService.gotoTargetingSearch();
                break;
            case Mode.wire:
                this.amplifyRoutingService.gotoWireHype();
                break;
            case Mode.editorial:
                this.amplifyRoutingService.gotoEditorialHype();
                break;
            case Mode.newsroom:
                this.router.navigate([StoriesConstants.newsroomRootPath]);
                break;
            case Mode.visibilityIntelligence:
                this.router.navigate(['/visibility-intelligence']);
                break;
            default:
                break;
        }
    }

    /**
     * Gets currently available items in the list
    */
    getListItems(): Observable<LeftSidebarListItem[]> {
        return this.listItemsProvider;
    }

    getListIsComplete(): Observable<boolean> {
        return this.listIsCompleteProvider;
    }

    getMoreListItems(): void {
        // not needed for amplify yet
    }

    getSelectedListItem(): Observable<LeftSidebarListItem> {
        return this.selectedListItemProvider;
    }

    /**
     * Responds to a user selecting a list item by taking the action that corresponds with the current mode
     */
    selectListItem(item: LeftSidebarListItem): void {
        switch (this.currentMode) {
            case Mode.searches:
                if (+this.currentRoute.value === item.value && this.searchDirty) {
                    this.targetingSearchesService.canDeactivate$().subscribe(confirm => {
                        if (confirm) {
                            this.discoverRoutingService.gotoTargetingSearchResults(item.value);
                        }
                    });
                } else {
                    this.discoverRoutingService.gotoTargetingSearchResults(item.value);
                }
                break;
            case Mode.distributions:
                if (item.value === DistributionSummaryModel.distributionDashboardId) {
                    this.discoverRoutingService.gotoDistributionDashboard();
                } else if (item.status !== DistributionStatus.draft) {
                    this.discoverRoutingService.gotoDistributionReport(item.value);
                } else {
                    // Clear this or when the component inits, it will see the previous
                    // value.  If that value is "sent" then it will redirect to the report page
                    // and you will be stuck, unable to view your item
                    this.distributionService.setFocusedDistribution(null);
                    this.discoverRoutingService.gotoDistribution(item.value);
                }
                break;
            case Mode.targetingLists:
                if (item.value === TargetingListList.allPrivateContactsId) {
                    this.discoverRoutingService.gotoPrivateContacts();
                } else {
                    this.discoverRoutingService.gotoListSearchResults(item.value);
                }
                break;
        }
    }


    /**
     * Responds to the user performing an action from a sidebar list item based on the current mode
     */
    listItemAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean = true): void {
        switch (this.currentMode) {
            case Mode.searches:
                this.targetingSearchAction(item, button, fromLHP);
                break;
            case Mode.targetingLists:
                this.targetingListAction(item, button, fromLHP);
                break;
            case Mode.distributions:
                this.distributionAction(item, button, fromLHP);
                break;
        }
    }

    /**
    * Opens GroupSelectionDialog for user selection then adds item to the selected group.
    * @param item item to move on confirm
    */
    public moveListItem(item: LeftSidebarListItem) {
        const groupId = item.groupId ? item.groupId : null;
        const showUncategorized = !!item.groupId;
        this.displayGroupsService.openGroupSelectionDialog(groupId, showUncategorized).subscribe((group: DisplayGroupModel) => {
            switch(this.currentMode) {
                case Mode.searches:
                case Mode.discoverContacts:
                    // @HACK - double subscribe, wait for all services are refactored
                    // eslint-disable-next-line rxjs/no-nested-subscribe
                    this.targetingSearchesService.updateGroup([item.value], group, true).subscribe();
                    break;
                case Mode.distributions:
                    this.distributionService.updateGroup([item.value], group);
                    break;
                case Mode.privateContacts:
                case Mode.targetingLists:
                    // @HACK temp until all services are refactored
                    // eslint-disable-next-line rxjs/no-nested-subscribe
                    this.targetingListService.updateGroup([item.value], group, true).subscribe();
                    break;
            }
        });
    }

    /**
     * Handles removing the groupId from all items in a deleted group
     * @param listItems items from the deleted group
     */
    public handleGroupDelete(ids: number[]) {
        switch(this.currentMode) {
            case Mode.searches:
            case Mode.discoverContacts:
                this.targetingSearchesService.removeSearchFromGroup(ids).subscribe();
                break;
            case Mode.distributions:
                this.distributionService.removeAllDistributionsFromGroup(ids);
                break;
            case Mode.privateContacts:
            case Mode.targetingLists:
                this.targetingListService.removeListsFromGroup(ids).subscribe();
                break;
        }
    }

    getContactSearchButtons(targetingSearch: TargetingSearch): LeftSidebarListItemButton[] {
        return [
            this.itemActionEdit,
            this.itemActionDelete
        ];
    }

    /**
     * Performs an action on a wire list item based on the provided action and item
     */
    private targetingSearchAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const searchId = item.value;
        const searchName = item.label;
        const searchListItem = this.targetingSearches.find(search => search.id === searchId);

        switch (button.key) {
            case ActionsEnum.edit:
                this.discoverRoutingService.gotoTargetingSearch(item.value);
                break;
            case ActionsEnum.delete: {
                const io: ConfirmDialogModel = {
                    inputs: {
                        id: 'discover.deleteSearchConfirmDialog',
                        heading:  'Discover.confirmDialog.deleteSearch.heading',
                        message:  'Discover.confirmDialog.deleteSearch.message',
                        list: [searchName],
                        okLabel: 'Tags.manageDialog.delete'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.targetingSearchesService.delete(searchId).subscribe();
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);

                break;
            }
            case ActionsEnum.show:
            case ActionsEnum.hide:
                this.targetingSearchesService.updateVisibility(item.value, button.key).subscribe();
                break;
            case ActionsEnum.copy:
                this.targetingSearchesService.openCopyTargetingSearchDialog(searchListItem);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    const io: TextInputDialogModel = {
                        inputs: {
                            id: 'renameSearch',
                            heading: 'Discover.dialogs.renameSearch.header',
                            description: 'Discover.dialogs.renameSearch.description',
                            placeholder: 'Discover.dialogs.renameSearch.placeholder',
                            initialValue: searchListItem.name,
                            okLabel: 'Lhp.actions.rename',
                            observableFormValidator: (name: string) => {
                                return this.targetingSearchesService.isNameDuplicated$(name, searchId).pipe(
                                    map((isDuplicate) => {
                                        if (isDuplicate) {
                                            return ['Discover.dialogs.saveSearch.duplicate'];
                                        } else {
                                            return [];
                                        }
                                    })
                                );
                            },
                            submit: (name: string) => {
                                return this.targetingSearchesService.rename(searchId, name);
                            }
                        }
                    };

                    this.coreDialogService.open(TextInputDialogComponent, io);
                } else {
                    this.targetingSearchesService.renameListDupCheck(item.value, searchName).subscribe();
                }
                break;
            case ActionsEnum.favorite:
                this.targetingSearchesService.updateFavorites([item.value], []).subscribe();
                break;
            case ActionsEnum.unfavorite:
                this.targetingSearchesService.updateFavorites([], [item.value]).subscribe();
                break;
        }
    }

    private targetingListAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const listListItem = this.targetingLists.find(list => list.id === item.value);
        const listId = item.value;
        const listName = item.label;

        switch (button.key) {
            case ActionsEnum.delete: {
                const io: ConfirmDialogModel = {
                    inputs: {
                        id: 'discover.deleteListConfirmDialog',
                        heading:  'Discover.confirmDialog.deleteList.heading',
                        message:  'Discover.confirmDialog.deleteList.message',
                        list: [listName],
                        okLabel: 'Tags.manageDialog.delete'
                    },
                    outputs: {
                        dialogClose: (confirmed: boolean) => {
                            if (confirmed) {
                                this.targetingListService.delete(listId).subscribe();
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, io);

                break;
            }
            case ActionsEnum.show:
            case ActionsEnum.hide:
                this.targetingListService.updateVisibility(listId, button.key).subscribe();
                break;
            case ActionsEnum.copy:
                this.targetingListService.copyTargetingList(listListItem);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    const io: TextInputDialogModel = {
                        inputs: {
                            heading: 'Discover.dialogs.editList.renameList',
                            initialValue: listName,
                            description: 'Discover.dialogs.editList.renameList.label',
                            placeholder: 'Discover.dialogs.editList.renameList.placeholder',
                            id: 'renameList',
                            submit: (name: string) => {
                                return this.targetingListService.rename(listId, name);
                            },
                            observableFormValidator: (name: string) => {
                                return this.targetingListService.isNameDuplicated$(name, listId).pipe(
                                    map((isDuplicate) => {
                                        if (isDuplicate) {
                                            return ['Discover.dialogs.newList.duplicate'];
                                        } else {
                                            return [];
                                        }
                                    })
                                );
                            }
                        }
                    };
                    this.coreDialogService.open(TextInputDialogComponent, io);

                } else {
                    this.targetingListService.renameListDupCheck(listId, listName).subscribe();
                }
                break;
            case ActionsEnum.favorite:
                this.targetingListService.updateFavorites([listId], []).subscribe();
                break;
            case ActionsEnum.unfavorite:
                this.targetingListService.updateFavorites([], [listId]).subscribe();
                break;
        }
    }

    private distributionAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const distributionListItem = this.distributions.find(distribution => distribution.id === item.value);
        switch (button.key) {
            case ActionsEnum.delete:
                this.distributionService.deleteDistribution(item.value, item.label, <DistributionStatus>item.status);
                break;
            case ActionsEnum.show:
            case ActionsEnum.hide:
                this.distributionService.updateDistributionVisibility(item.value, button.key);
                break;
            case ActionsEnum.copy:
                this.distributionService.copyDistribution(distributionListItem);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    this.distributionService.renameDistribution(item.value, item.label);
                } else {
                    this.distributionService.updateDistributionName(item.value, item.label);
                }
                break;
            case ActionsEnum.favorite:
                this.distributionService.updateFavorites([item.value], []);
                break;
            case ActionsEnum.unfavorite:
                this.distributionService.updateFavorites([], [item.value]);
                break;
        }
    }

    /**
     * Gets a provider for when reordering the left sidebar is enabled
     */
    getReorderEnabled(): Observable<boolean> {
        return this.reorderEnabledProvider;
    }

    /**
     * Sets the left sidebar's order
     */
    setListItems(items: LeftSidebarListItem[], listIsComplete?: boolean) {
        // for re-ordering
    }

    /**
     * Checks if the primary button should be enabled.
     */
    public isAmplifyButtonEnabled(): Observable<boolean> {
        return this.buttonEnabledProvider;
    }

    /**
     * Responds to the user clicking the primary call to action
     * in the sidebar
    */
    amplifyButtonClicked(): void {
        this.currentModeConfig.amplifyAction();
    }

    /**
     * Gets the current localizations for the left sidebar
     */
    getLocalizations(): Observable<LeftSidebarLocalizations> {
        return this.localizationsProvider;
    }

    /**
     * Sets the current localizations
     */
    private setLocalizations(localizations: LeftSidebarLocalizations): void {
        this.localizations = localizations;
        this.localizationsProvider.next(localizations);
    }

    /**
     * Assumes that a and b has modifiedDate and createdDate, returns negative if a is newer, 0 if equal, positive if b is larger
     */
    private sortByLastModified(a, b): number {
        return (+b.modifiedDate || +b.createdDate) - (+a.modifiedDate || +a.createdDate);
    }

    /**
     * Returns 1 if a's label should be listed before b's label, -1 if reverse is true, 0 if the same
     */
    private sortByLabel(a, b): number {
        return a.label.localeCompare(b.label);
    }

    public getSearchTypeFilterCounts(searches: TargetingSearchList[], typeFilter: FilterTypeCheckboxModel<SearchModeEnum> = this.searchTypeFilter): void {
        const optionsMap: Map<SearchModeEnum, FilterOptionModel<SearchModeEnum>> = new Map<SearchModeEnum, FilterOptionModel<SearchModeEnum>>();
        typeFilter.options.forEach(opt => {
            opt.count = 0;
            optionsMap.set(opt.value, opt);
        });
        searches.forEach(search => {
            const filterOption = optionsMap.get(search.type);
            if (filterOption) {
                filterOption.count++;
            }
        });
    }

    public applySearchTypeFitler(
        items: TargetingSearchList[],
        typeFilter: FilterTypeCheckboxModel<SearchModeEnum> = this.searchTypeFilter
    ): TargetingSearchList[] {
        const types = typeFilter.getValue();

        if (!types || types.length < 1) {
            return items;
        }

        return items.filter(item => {
            return types.includes(item.type);
        });
    }

    public getListTypeFilterCounts(lists: TargetingListList[], typeFilter: FilterTypeCheckboxModel<TargetedListTypesEnum> = this.listTypeFilter): void {
        const optionsMap: Map<TargetedListTypesEnum, FilterOptionModel<TargetedListTypesEnum>>
            = new Map<TargetedListTypesEnum, FilterOptionModel<TargetedListTypesEnum>>();
        typeFilter.options.forEach(opt => {
            opt.count = 0;
            optionsMap.set(opt.value, opt);
        });
        lists.forEach(list => {
            if (list.hasContacts && list.hasOutlets) {
                optionsMap.get(TargetedListTypesEnum.both).count++;
            } else if (list.hasContacts) {
                optionsMap.get(TargetedListTypesEnum.contact).count++;
            } else if (list.hasOutlets) {
                optionsMap.get(TargetedListTypesEnum.outlet).count++;
            } else if (list?.count === 0) {
                optionsMap.get(TargetedListTypesEnum.empty).count++;
            }
        });
    }
    public applyListTypeFilters(
        items: TargetingListList[],
        typeFilter: FilterTypeCheckboxModel<TargetedListTypesEnum> = this.listTypeFilter
    ): TargetingListList[] {
        const types = typeFilter.getValue();

        if (!types || types.length < 1) {
            return items;
        }

        const both = types.includes(TargetedListTypesEnum.both);
        const hasContact = types.includes(TargetedListTypesEnum.contact);
        const hasOutlet = types.includes(TargetedListTypesEnum.outlet);
        const empty = types.includes(TargetedListTypesEnum.empty);

        return items.filter(item => {
            const isGood = (both && item.hasContacts && item.hasOutlets)
                || (hasContact && item.hasContacts && !item.hasOutlets)
                || (hasOutlet && item.hasOutlets && !item.hasContacts)
                || (empty && !item.hasContacts && !item.hasOutlets);

            return isGood;
        });
    }

    public getDeliveryStatusTypeFilterCounts(
        items: DistributionSummaryModel[],
        statusFilter: FilterTypeCheckboxModel<DistributionStatus> = this.deliveryStatusFilter
    ) {
        const optionsMap: Map<DistributionStatus, FilterOptionModel<DistributionStatus>> = new Map();
        statusFilter.options.forEach(opt => {
            opt.count = 0;
            optionsMap.set(opt.value, opt);
        });
        items.forEach(item => {
            if (item.status) { // TODO: Figure out what to do when status is undefined
                optionsMap.get(item.status).count++;
            }
        });
    }

    public applyStatusFilters(
        items: TargetingListList[],
        statusFilter: FilterTypeCheckboxModel<DistributionStatus> = this.deliveryStatusFilter
    ): TargetingListList[] {
        const filterData = statusFilter.getValue();
        if (!filterData || filterData.length < 1) {
            return items;
        }

        return items.filter(item => {
            const status = item.toLeftSidebarListItem().status as DistributionStatus;
            return filterData.includes(status);
        });
    }


    applyFilters(): void {
        super.applyFilters();

        if (this.currentModeConfig && this.currentModeConfig.applyFilters) {
            const hiddenItems = this.getHiddenItemsFromFilteredItems(this.currentModeConfig.listItemSource);
            const hiddenLHPItems: LeftSidebarListItem[] = hiddenItems.map(item => item.toLeftSidebarListItem(this.currentModeConfig.getButtons(item)));
            this.hiddenListItemsProvider.next(hiddenLHPItems);
            const items = this.currentModeConfig.applyFilters(this.currentModeConfig.listItemSource);
            const listItems: LeftSidebarListItem[] = items.map(item => item.toLeftSidebarListItem(this.currentModeConfig.getButtons(item)));

            this.listItems = listItems;
            this.groupListItems();
            this.listItemsProvider.next(this.listItems);
            this.listIsComplete = this.currentModeConfig.isComplete;
            this.listIsCompleteProvider.next(this.listIsComplete);
        }
    }


    private getHiddenItemsFromFilteredItems(
        items: LeftSidebarListItemConvertableModel[]
    ): LeftSidebarListItemConvertableModel[] {
        const value = this.visibilityFilter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
            case ListEntitiesVisibilityEnum.hidden:
                // There are no "hidden" items that are not currently showing if we are explicitly showing them
                return [];
            case ListEntitiesVisibilityEnum.visible:
            default:
                // can be null as there are no options/starting value for the visibility filter
                return items.filter(item  => (item as GenericLeftSidebarItem).hidden);
        }
    }

    getHiddenListItems(): Observable<LeftSidebarListItem[]> {
        return this.hiddenListItemsProvider;
    }


    private applyVisibilityFilter<T extends AuditableModel>(items: T[]): T[] {
        const value = this.visibilityFilter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
                return items;
            case ListEntitiesVisibilityEnum.visible:
                return items.filter(item => !item.hidden);
            case ListEntitiesVisibilityEnum.hidden:
                return items.filter(item => item.hidden);
            default:
                return items.filter(item => !item.hidden);
        }
    }

    setVisibilityFilter(visibility: ListEntitiesVisibilityEnum = ListEntitiesVisibilityEnum.all) {
        this.visibilityFilter.setCurrentValue(visibility);
        this.applyFilters();
    }

    /**
     * Based on the current mode, returns an observable that sets and prepares the current filters.
     */
    private getItemsAndPrepareFilters(currentMode: Mode): Observable<void> {
        let getItems$: Observable<DistributionSummaryModel[] | TargetingSearchList[] | TargetingListList[]>;

        if (currentMode === Mode.distributions) {
            getItems$ = this.distributionService.getDistributionSummaries();
        } else if (currentMode === Mode.searches) {
            getItems$ = this.targetingSearchesService.all$;
        } else if (currentMode === Mode.targetingLists) {
            getItems$ = this.targetingListService.all$;
        } else {
            return NEVER;
        }
        const type = this.modeToGroupTypeMap.get(this.currentMode);
        const getGroups$ = this.displayGroupsService.setTypeAndGetGroups(type);

        // When the list items emit, we call prepareFilters(). prepareFilters causes an inner subscription in
        // getAuditableFilterOptions. We need a combineLatest here (instead of simple piping) because of this
        // (We need to call the function but also have a subscription set up to handle it if it happens synchronously)
        // TODO: Clean up the getAuditableFilterOptions method to allow easier composition
        return combineLatest([
            this.auditableFilterOptionsComplete$.pipe(
                take(1)
            ),
            getItems$.pipe(
                filter(x => !!x),
                tap(() => {
                    const config = this.discoverLeftSidebarModeConfigMap.get(currentMode);
                    this.setFilters(config.filters);
                    config.prepareFilters();
                    this.getVisibilityFilterOptions(this.visibilityFilter);
                })
            ),
            getGroups$.pipe(
                map(groups => {
                    this.listItemGroups = groups.slice();
                })
            )
        ]).pipe(
            mapTo(null)
        );
    }

    /**
     * Resets all of the filters
     */
    private resetFilterOptions(): void {
        this.nameFilter.setValue([]);
        this.searchTypeFilter.setValue([]);
        this.listTypeFilter.setValue([]);
        this.createdByFilter.setValue([]);
        this.createdByFilter.options = [];
        this.createdDateFilter.setValue(DateQueryModel.getAllQuery());
        this.modifiedByFilter.setValue([]);
        this.modifiedByFilter.options = [];
        this.modifiedDateFilter.setValue(DateQueryModel.getAllQuery());
        this.deliveryStatusFilter.setValue([]);
    }
    //#endregion

}
